import { useCallback, useState, useEffect } from 'react'
import { ClockIcon } from '@heroicons/react/24/solid'
import { Tooltip } from 'flowbite-react'
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom'
import { useLazyQuery, useMutation, useReactiveVar, gql } from '@apollo/client'
import { v4 as uuidv4 } from 'uuid'
import { toast } from 'react-toastify'

import { apolloAuthData } from '../services/apollo'
import { ROLE_MANAGER } from '../constants'
import { RESTORE_POST_MUTATION } from '../services/admin'
import { ConfirmDialog } from '../components'

const GET_HOME_PAGE_DATABASE_ID = gql`
  query HomePages {
    homePages(first: 1) {
      edges {
        node {
          databaseId
        }
      }
    }
  }
`
const GET_AGENDA_PAGE_DATABASE_ID = gql`
  query AgendaPages {
    agendaPages(first: 1) {
      edges {
        node {
          databaseId
        }
      }
    }
  }
`
const GET_AGENDA_ITEM_DATABASE_ID = gql`
  query AgendaItems($postId: String!) {
    agendaItems(where: { postId: $postId }) {
      edges {
        node {
          databaseId
        }
      }
    }
  }
`
const GET_TIPS_TOOLS_PAGE_DATABASE_ID = gql`
  query TipsToolsPages {
    tipsToolsPages(first: 1) {
      edges {
        node {
          databaseId
        }
      }
    }
  }
`
const GET_TIPS_TOOLS_ITEM_DATABASE_ID = gql`
  query TipsToolsItem($postId: String!) {
    tipsToolsItems(where: { postId: $postId }) {
      edges {
        node {
          databaseId
        }
      }
    }
  }
`
const GET_LEARNING_LOUNGE_PAGE_DATABASE_ID = gql`
  query LearningLoungePages {
    learningLoungePages(first: 1) {
      edges {
        node {
          databaseId
        }
      }
    }
  }
`
const GET_LEARNING_LOUNGE_ITEM_DATABASE_ID = gql`
  query LearningLoungeItem($postId: String!) {
    learningLoungeItems(where: { postId: $postId }) {
      edges {
        node {
          databaseId
        }
      }
    }
  }
`

const Rollback = () => {
  const auth = useReactiveVar(apolloAuthData)
  const isManager = auth.user?.capabilities?.includes(ROLE_MANAGER)
  let [searchParams] = useSearchParams()
  let location = useLocation()
  let navigate = useNavigate()
  const isHomePage = location.pathname === '/'
  const isAgendaPage = location.pathname.startsWith('/agenda')
  const isTipsToolsPage = location.pathname === '/tipstools'
  const isTipsToolsItemPage = location.pathname.match(/tipstools\/\w+/)
  const isLearningLoungePage = location.pathname === '/learninglounge'
  const isLearningLoungeItemPage =
    location.pathname.match(/learninglounge\/\w+/)
  const isEditOn = isManager && !!searchParams.get('edit')
  const [confirmRestore, setConfirmRestore] = useState(false)
  const [agendaPageTopSectionSelected, setAgendaPageTopSectionSelected] =
    useState(false)
  const [agendaPageBottomSectionSelected, setAgendaPageBottomSectionSelected] =
    useState(false)
  const [restorePost, { loading: restoringPost }] = useMutation(
    RESTORE_POST_MUTATION
  )
  const [getHomePageDatabaseId, { loading: gettingHomePageDatabaseId }] =
    useLazyQuery(GET_HOME_PAGE_DATABASE_ID, {
      fetchPolicy: 'no-cache',
    })
  const [getAgendaPageDatabaseId, { loading: gettingAgendaPageDatabaseId }] =
    useLazyQuery(GET_AGENDA_PAGE_DATABASE_ID, {
      fetchPolicy: 'no-cache',
    })
  const [getAgendaItemDatabaseId, { loading: gettingAgendaItemDatabaseId }] =
    useLazyQuery(GET_AGENDA_ITEM_DATABASE_ID, {
      fetchPolicy: 'no-cache',
      variables: {
        postId: isAgendaPage ? location.pathname.split('/').pop() : '',
      },
    })
  const [
    getTipsToolsPageDatabaseId,
    { loading: gettingTipsToolsPageDatabaseId },
  ] = useLazyQuery(GET_TIPS_TOOLS_PAGE_DATABASE_ID, {
    fetchPolicy: 'no-cache',
  })
  const [
    getTipsToolsItemDatabaseId,
    { loading: gettingTipsToolsItemDatabaseId },
  ] = useLazyQuery(GET_TIPS_TOOLS_ITEM_DATABASE_ID, {
    fetchPolicy: 'no-cache',
    variables: {
      postId: isTipsToolsItemPage ? location.pathname.split('/').pop() : '',
    },
  })
  const [
    getLearningLoungePageDatabaseId,
    { loading: gettingLearningLoungePageDatabaseId },
  ] = useLazyQuery(GET_LEARNING_LOUNGE_PAGE_DATABASE_ID, {
    fetchPolicy: 'no-cache',
  })
  const [
    getLearningLoungeItemDatabaseId,
    { loading: gettingLearningLoungeItemDatabaseId },
  ] = useLazyQuery(GET_LEARNING_LOUNGE_ITEM_DATABASE_ID, {
    fetchPolicy: 'no-cache',
    variables: {
      postId: isLearningLoungeItemPage
        ? location.pathname.split('/').pop()
        : '',
    },
  })

  const handleRestoreClick = () => {
    setConfirmRestore(true)
  }

  const handleConfirmRestoreCancel = () => {
    setConfirmRestore(false)
  }

  /**
   * Start the rollback process
   */
  const startRollback = useCallback(
    async (id, navigateTo = '/') => {
      try {
        await restorePost({
          variables: {
            clientMutationId: uuidv4(),
            id,
          },
        })

        // Refresh the page
        // navigate(0)
        if (navigateTo !== '') {
          navigate(navigateTo, { state: { data: 'reload' } })
        }
      } catch (err) {
        if (err.message.includes('no backup post to restore')) {
          toast.warning(
            `Rollback stopped. There's no backup available for this page at the moment.`
          )
        } else {
          toast.error(err.message ?? 'Rollback failed due to unknown error.')
        }
        console.log('Revert failed', err)

        // Close the dialog box
        setConfirmRestore(false)
      }
    },
    [navigate, restorePost]
  )

  /**
   * Restore home page
   */
  const restoreHomePage = useCallback(async () => {
    try {
      // Retrieve the post ID
      const homePageDatabaseIdResult = await getHomePageDatabaseId()
      const homePageDatabaseId =
        homePageDatabaseIdResult?.data?.homePages?.edges[0]?.node?.databaseId

      // Return if there's no post ID
      if (typeof homePageDatabaseId !== 'number') {
        toast.error(`Rollback failed. Post ID can't be determined.`)

        // Close the dialog box
        setConfirmRestore(false)

        return
      }

      // Start the rollback process
      await startRollback(homePageDatabaseId, '/')
    } catch (err) {
      console.log('-> err', err)
      throw err
    }
  }, [getHomePageDatabaseId, startRollback])

  /**
   * Restore agenda page
   */
  const restoreAgendaPage = useCallback(async () => {
    try {
      // Retrieve the post ID
      const agendaPageDatabaseIdResult = await getAgendaPageDatabaseId()
      const agendaPageDatabaseId =
        agendaPageDatabaseIdResult?.data?.agendaPages?.edges[0]?.node
          ?.databaseId

      // Return if there's no post ID
      if (!agendaPageBottomSectionSelected) {
        if (typeof agendaPageDatabaseId !== 'number') {
          toast.error(`Rollback failed. Post ID can't be determined.`)

          // Close the dialog box
          setConfirmRestore(false)

          return
        }
      }

      // Start the rollback process
      if (typeof agendaPageDatabaseId === 'number') {
        await startRollback(
          agendaPageDatabaseId,
          agendaPageBottomSectionSelected ? '' : location.pathname
        )
      }
    } catch (err) {
      console.log('-> err', err)
      throw err
    }
  }, [
    getAgendaPageDatabaseId,
    startRollback,
    agendaPageBottomSectionSelected,
    location.pathname,
  ])

  /**
   * Restore agenda item
   */
  const restoreAgendaItem = useCallback(async () => {
    try {
      // Retrieve the post ID
      const agendaItemDatabaseIdResult = await getAgendaItemDatabaseId()
      const agendaItemDatabaseId =
        agendaItemDatabaseIdResult?.data?.agendaItems?.edges[0]?.node
          ?.databaseId

      // Return if there's no post ID
      if (typeof agendaItemDatabaseId !== 'number') {
        toast.error(`Rollback failed. Post ID can't be determined.`)

        // Close the dialog box
        setConfirmRestore(false)

        return
      }

      // Start the rollback process
      await startRollback(agendaItemDatabaseId, location.pathname)
    } catch (err) {
      console.log('-> err', err)
      throw err
    }
  }, [getAgendaItemDatabaseId, startRollback, location.pathname])

  /**
   * Restore tips and tools page
   */
  const restoreTipsToolsPage = useCallback(async () => {
    try {
      // Retrieve the post ID
      const tipsToolsPageDatabaseIdResult = await getTipsToolsPageDatabaseId()
      const tipsToolsPageDatabaseId =
        tipsToolsPageDatabaseIdResult?.data?.tipsToolsPages?.edges[0]?.node
          ?.databaseId

      // Return if there's no post ID
      if (typeof tipsToolsPageDatabaseId !== 'number') {
        toast.error(`Rollback failed. Post ID can't be determined.`)

        // Close the dialog box
        setConfirmRestore(false)

        return
      }

      // Start the rollback process
      await startRollback(tipsToolsPageDatabaseId, '/tipstools')
    } catch (err) {
      console.log('-> err', err)
      throw err
    }
  }, [getTipsToolsPageDatabaseId, startRollback])

  /**
   * Restore tips & tools item
   */
  const restoreTipsToolsItem = useCallback(async () => {
    try {
      // Retrieve the post ID
      const tipsToolsItemDatabaseIdResult = await getTipsToolsItemDatabaseId()
      const tipsToolsItemDatabaseId =
        tipsToolsItemDatabaseIdResult?.data?.tipsToolsItems?.edges[0]?.node
          ?.databaseId

      // Return if there's no post ID
      if (typeof tipsToolsItemDatabaseId !== 'number') {
        toast.error(`Rollback failed. Post ID can't be determined.`)

        // Close the dialog box
        setConfirmRestore(false)

        return
      }

      // Start the rollback process
      await startRollback(tipsToolsItemDatabaseId, location.pathname)
    } catch (err) {
      console.log('-> err', err)
      throw err
    }
  }, [getTipsToolsItemDatabaseId, startRollback, location.pathname])

  /**
   * Restore learning lounge page
   */
  const restoreLearningLoungePage = useCallback(async () => {
    try {
      // Retrieve the post ID
      const learningLoungePageDatabaseIdResult =
        await getLearningLoungePageDatabaseId()
      const learningLoungePageDatabaseId =
        learningLoungePageDatabaseIdResult?.data?.learningLoungePages?.edges[0]
          ?.node?.databaseId

      // Return if there's no post ID
      if (typeof learningLoungePageDatabaseId !== 'number') {
        toast.error(`Rollback failed. Post ID can't be determined.`)

        // Close the dialog box
        setConfirmRestore(false)

        return
      }

      // Start the rollback process
      await startRollback(learningLoungePageDatabaseId, '/learninglounge')
    } catch (err) {
      console.log('-> err', err)
      throw err
    }
  }, [getLearningLoungePageDatabaseId, startRollback])

  /**
   * Restore learning lounge item
   */
  const restoreLearningLoungeItem = useCallback(async () => {
    try {
      // Retrieve the post ID
      const learningLoungeItemDatabaseIdResult =
        await getLearningLoungeItemDatabaseId()
      const learningLoungeItemDatabaseId =
        learningLoungeItemDatabaseIdResult?.data?.learningLoungeItems?.edges[0]
          ?.node?.databaseId

      // Return if there's no post ID
      if (typeof learningLoungeItemDatabaseId !== 'number') {
        toast.error(`Rollback failed. Post ID can't be determined.`)

        // Close the dialog box
        setConfirmRestore(false)

        return
      }

      // Start the rollback process
      await startRollback(learningLoungeItemDatabaseId, location.pathname)
    } catch (err) {
      console.log('-> err', err)
      throw err
    }
  }, [getLearningLoungeItemDatabaseId, startRollback, location.pathname])

  const handleRestore = async () => {
    if (isHomePage) {
      await restoreHomePage()
    } else if (isAgendaPage) {
      if (agendaPageTopSectionSelected) {
        await restoreAgendaPage()
      }

      if (agendaPageBottomSectionSelected) {
        await restoreAgendaItem()
      }
    } else if (isTipsToolsPage) {
      await restoreTipsToolsPage()
    } else if (isTipsToolsItemPage) {
      await restoreTipsToolsItem()
    } else if (isLearningLoungePage) {
      await restoreLearningLoungePage()
    } else if (isLearningLoungeItemPage) {
      await restoreLearningLoungeItem()
    }
  }
  useEffect(() => {
    if (location.state?.data === 'reload') {
      // Show the successful toast message
      toast.success(`Your changes have been rolled back successfully.`)
    }
  }, [navigate, location.state])

  /**
   * Handle agenda page top section checkbox
   *
   * @param {*} e - Event object
   */
  const handleAgendaPageTopSelectedChange = (e) => {
    setAgendaPageTopSectionSelected(e.target.checked)
  }

  /**
   * Handle agenda page bottom section checkbox
   *
   * @param {*} e - Event object
   */
  const handleAgendaPageBottomSelectedChange = (e) => {
    setAgendaPageBottomSectionSelected(e.target.checked)
  }

  return (
    <>
      {/** Rollback confirmation dialog */}
      {isEditOn && (
        <ConfirmDialog
          yesText="Roll back"
          noText="Cancel"
          open={confirmRestore}
          inProgress={
            gettingHomePageDatabaseId ||
            gettingAgendaPageDatabaseId ||
            gettingAgendaItemDatabaseId ||
            gettingTipsToolsPageDatabaseId ||
            gettingTipsToolsItemDatabaseId ||
            gettingLearningLoungePageDatabaseId ||
            gettingLearningLoungeItemDatabaseId ||
            restoringPost
          }
          inProgressButtonText="Rolling back..."
          enableYesButton={
            isAgendaPage
              ? agendaPageTopSectionSelected || agendaPageBottomSectionSelected
              : true
          }
          onConfirm={handleRestore}
          onCancel={handleConfirmRestoreCancel}
          // text="<p>Are you sure you want to roll back changes on this page?</p><p>This action cannot be undone.</p>"
          title="Roll back changes"
          text={
            <div>
              <p>Are you sure you want to roll back changes on this page?</p>
              <p className="font-bold text-red-500">
                This action cannot be undone.
              </p>
              {isAgendaPage && (
                <>
                  <p>Select section(s) you wish to roll back the changes:</p>
                  <div className="ml-2 flex items-center">
                    <input
                      type="checkbox"
                      id="agendaPage"
                      className="mr-2"
                      checked={agendaPageTopSectionSelected}
                      onChange={handleAgendaPageTopSelectedChange}
                    />
                    <label htmlFor="agendaPage">
                      Top half: Page title, overview, instruction above the day
                      tabs
                    </label>
                  </div>
                  <div className="ml-2 flex items-center">
                    <input
                      type="checkbox"
                      id="agendaDay"
                      className="mr-2"
                      checked={agendaPageBottomSectionSelected}
                      onChange={handleAgendaPageBottomSelectedChange}
                    />
                    <label htmlFor="agendaDay">
                      Bottom half: Modules and reflection point
                    </label>
                  </div>
                </>
              )}
            </div>
          }
        />
      )}
      <button className="group" onClick={handleRestoreClick}>
        <Tooltip
          // style="light"
          content={
            <div className="text-xs font-normal">
              Roll back published changes
            </div>
          }
        >
          <ClockIcon className="h-[30px] w-[30px] group-hover:fill-skin-button-accent-hover" />
        </Tooltip>
      </button>
    </>
  )
}

export default Rollback
